import React, { useState, useRef, useCallback, useEffect } from "react";
import "./style.css";

import Method from "../child/method/Method";
import SingleTooth from "../child/single-tooth/SingleTooth";
import { Col, message, Row } from "antd";
import MethodReference from "../child/method-reference/MethodReference";
import PhysicalRadioGraph from "../child/physical-radio-graph/PhysicalRadioGraph";
import StageCriteria from "../child/stage-criteria/StageCriteria";
import CustomButtonWithIcon from "../../../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import CustomPrimaryButton from "../../../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import { assessmentIcons } from "../../../../utils/assessmentIcons";
import { useSelector } from "react-redux";
import {
  getExamMethodInformation,
  submitPrimaryTeethExamResult,
  submitWisdomTeethExamResult,
} from "../../../../../components/https/age-assessment-apis";
import {
  errorRequestHandel,
  ageAssessmentBasePath,
} from "../../../../../helper";
import CustomSpinner from "../../../../../dentail-indetifcation/components/d-reusable/CustomSpiner";
import axios from "axios";
import MatchStageTable from "../child/match-stage-table/MatchStageTable";
import MatchTeethStageTable from "../child/match-stage-table/MatchTeethStageTable";
import { useNavigate } from "react-router-dom";
import Paragraph from "antd/lib/typography/Paragraph";
import { useSubscriptionGuard } from "../../../../../hooks/subscription-hook/UseSubscriptionGuard";

function WisdomTeethStaging() {
  const navigate = useNavigate();
  useSubscriptionGuard();

  const AVAILABLE_METHODS = useSelector(
    (state) => state.ageAssessmentCase?.exameMethods || []
  );
  const currentCaseDetail = useSelector(
    (state) => state.ageAssessmentCase?.currentCaseDetail || null
  );
  let additional_images = currentCaseDetail?.additional_images || [];
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Core state
  const [methodsData, setMethodsData] = useState({}); // Cache for API data

  const [selectedMethods, setSelectedMethods] = useState([
    AVAILABLE_METHODS[0].id,
  ]); // Default to Schour Method
  const [examMethodData, setExamMethodData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStageIndex, setCurrenStageIndex] = useState(0);
  const [currentToothIndex, setCurrenToothIndex] = useState(0);

  // Image controls
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRadioGraphFullscreen, setIsRadioGraphFullscreen] = useState(false);

  const [stages, setStages] = useState([]); // Current method's stages
  const [matchedStages, setMatchedStages] = useState([]);

  // Refs
  const imageContainerRef = useRef(null);
  const radioGraphContainerRef = useRef(null);
  // Fetch initial method data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      const defaultMethod = AVAILABLE_METHODS[0];
      if (defaultMethod) {
        await handleMethodToggle(defaultMethod.id);
      }
    };

    fetchInitialData();
  }, []);

  const fetchMethodData = async (slug) => {
    try {
      setLoading(true);
      const response = await getExamMethodInformation({ slug: slug });
      if (response.status === 200) {
        setExamMethodData(response.data.data);

        setMethodsData((prev) => ({
          ...prev,
          [slug]: response.data.data,
        }));
        setStages(response.data.data[0].stages);
        setLoading(false);
      }
    } catch (error) {
      errorRequestHandel({ error: error });
      setLoading(false);
    }
  };

  // Get current stage
  const currentStage = stages[currentStageIndex] || {
    id: 0,
    name: "",
    age: 0,
    deviation: 0,
    description: "Loading...",
    diagram: "/placeholder.svg?height=400&width=600&text=Loading",
    radiograph: "/placeholder.svg?height=400&width=600&text=Loading",
  };

  const handleMethodToggle = async (methodId) => {
    const method = AVAILABLE_METHODS.find((m) => m.id === methodId);
    if (!method) return;

    const isCollation = methodId === 17;

    const hasAnyProgress = () => {
      return selectedMethods.some((id) => {
        const status = getMethodStatus(id);
        return status === "completed" || status === "in-progress";
      });
    };

    const isCollationInProgress = () => {
      const status = getMethodStatus(17);
      return status === "completed" || status === "in-progress";
    };

    setSelectedMethods((prevSelected) => {
      // ✅ Case 1: Selecting Collation
      if (isCollation) {
        const switchingFromOthers =
          prevSelected.length > 0 && !prevSelected.includes(17);
        if (switchingFromOthers && hasAnyProgress()) {
          const userConfirmed = window.confirm(
            "You have in-progress or completed data in other methods. Selecting Collation will remove this progress. Do you want to continue?"
          );
          if (!userConfirmed) return prevSelected;
        }

        setMatchedStages([]);
        return [17];
      }

      // ✅ Case 2: Switching from Collation to another method
      if (prevSelected.includes(17)) {
        if (isCollationInProgress()) {
          const userConfirmed = window.confirm(
            "You have in-progress or completed data in Collation. Switching will remove this progress. Do you want to continue?"
          );
          if (!userConfirmed) return prevSelected;
        }

        setMatchedStages([]);
        return [methodId];
      }

      // ✅ Case 3: Regular Add Only
      if (!prevSelected.includes(methodId)) {
        return [...prevSelected, methodId];
      }

      return prevSelected; // Don't unselect if already selected
    });

    setSelectedMethod(method);
    setCurrenToothIndex(0);
    setCurrenStageIndex(0);

    if (!methodsData[method.slug]) {
      await fetchMethodData(method.slug);
    } else {
      setExamMethodData(methodsData[method.slug]);
      setStages(methodsData[method.slug][0].stages);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (imageContainerRef.current?.requestFullscreen) {
        imageContainerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };
  // Fullscreen toggle
  const toggleRadioGrapghFullscreen = () => {
    if (!isRadioGraphFullscreen) {
      if (radioGraphContainerRef.current?.requestFullscreen) {
        radioGraphContainerRef.current.requestFullscreen();
        setIsRadioGraphFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsRadioGraphFullscreen(false);
      }
    }
  };

  // Stage matching handler

  const handleStageMatch = (selectedStageId) => {
    const tooth = examMethodData[currentToothIndex];
    const method = selectedMethod;

    setMatchedStages((prev) => {
      // Find if the method already exists
      const methodIndex = prev.findIndex((m) => m.method.id === method.id);

      if (methodIndex === -1) {
        // Method does not exist, add new method with the selected stage object
        return [
          ...prev,
          {
            method, // Store the full method object
            teeth: [
              {
                ...tooth,
                stage: tooth.stages.find((s) => s.id === selectedStageId), // Find correct stage
              },
            ],
          },
        ];
      } else {
        // Method exists, check if the tooth already has a stage
        const updatedMethods = [...prev];
        const toothIndex = updatedMethods[methodIndex].teeth.findIndex(
          (t) => t.id === tooth.id
        );

        if (toothIndex === -1) {
          // Tooth does not exist, add new tooth with full stage object
          updatedMethods[methodIndex].teeth.push({
            ...tooth,
            stage: tooth.stages.find((s) => s.id === selectedStageId), // Find correct stage
          });
        } else {
          // Tooth exists, update the stage with full object
          updatedMethods[methodIndex].teeth[toothIndex].stage =
            tooth.stages.find((s) => s.id === selectedStageId);
        }

        return updatedMethods;
      }
    });

    // select next tooth

    if (currentToothIndex < examMethodData.length - 1) {
      const nextToothIndex = currentToothIndex + 1;
      if (!isToothStageMatched(examMethodData[nextToothIndex].id)) {
        setCurrenToothIndex(nextToothIndex);
        setStages(examMethodData[nextToothIndex].stages);
        setCurrenStageIndex(0);
      }
    }

    message.success("Stage matched successfully!");
  };

  const transformData = (project_id, inputArray) => {
    return {
      project_id: project_id,
      methods: inputArray.map((method) => ({
        id: method.method.id,
        teeth: method.teeth.map((tooth) => ({
          id: tooth.id,
          stage_id: tooth.stage.id,
        })),
      })),
    };
  };

  // Handle submission
  const handleSubmit = async () => {
    if (Object.keys(matchedStages).length === 0) {
      message.warning("Please complete atleast one method to proceed.");
      return;
    }

    const payload = transformData(currentCaseDetail?.project_id, matchedStages);

    try {
      setIsLoading(true);

      const response = await submitWisdomTeethExamResult({ payload: payload });
      if (response.status === 200) {
        message.success("Results submitted successfully!");
        navigate(`${ageAssessmentBasePath}/cases/details`, {
          state: { projectId: currentCaseDetail?.project_id },
        });
        setIsLoading(false);
      }

      // Here you might redirect or do something else on success
    } catch (error) {
      errorRequestHandel({ error: error });
      setIsLoading(false);
    }
  };

  const isToothStageMatched = (toothId) => {
    // Find the method in matchedStages
    const method = matchedStages.find(
      (m) => m.method?.id === selectedMethod.id
    );

    // If the method exists, check if the tooth is present
    if (method) {
      return method.teeth.some((tooth) => tooth.id === toothId);
    }
    // If the method does not exist, return false
    return false;
  };

  const getMethodStatus = (methodId) => {
    // Find the method in matchedStages
    const method = matchedStages.find((m) => m.method.id === methodId);

    // If the method exists in matchedStages
    if (method) {
      const matchedTeethCount = method.teeth.length;

      // If all 4 teeth are matched, return "completed"
      if (matchedTeethCount === 4) return "completed";

      // If at least one tooth is matched, return "in-progress"
      if (matchedTeethCount > 0) return "in-progress";
    }

    // If the method is selected but has no matched teeth yet, return "selected"
    if (selectedMethods.includes(methodId)) return "selected";

    // Otherwise, return "none"
    return "none";
  };

  const isStageMatched = (stageId) => {
    // Find the method in matchedStages
    const matchedMethod = matchedStages.find(
      (m) => m.method.id === selectedMethod?.id
    );

    // Get the current tooth ID
    const toothId = examMethodData[currentToothIndex]?.id;
    // Ensure method and tooth exist before checking stages
    if (matchedMethod && toothId) {
      return matchedMethod?.teeth?.some(
        (tooth) => tooth.id === toothId && tooth?.stage?.id === stageId
      );
    }

    // If method does not exist or tooth-stage not found, return false
    return false;
  };

  const handleDeleteMatchStageRecord = (methodId, toothId) => {
    setMatchedStages(
      (prevData) =>
        prevData
          .map((item) =>
            item.method.id === methodId
              ? {
                  ...item,
                  teeth: item.teeth.filter((tooth) => tooth.id !== toothId),
                }
              : item
          )
          .filter((item) => item.teeth.length > 0) // Remove method if no teeth left
    );
  };

  const handleOnChangeTooth = (index) => {
    setCurrenToothIndex(index);
    setStages(examMethodData[index].stages);
  };

  const onCancel = () => {
    if (Object.keys(matchedStages).length > 0) {
      const confirmCancel = window.confirm("Are you sure you want to cancel?");
      if (!confirmCancel) {
        return; // Stop if the user cancels
      }
    }
    window.history.back(); // Go back to the previous page
  };

  const handleClickOnTableTab = (id) => {
    handleMethodToggle(id);
  };

  return (
    <div className="age-estimation-interface">
      <CustomSpinner loading={loading} />
      {/* Method Selection */}
      <section className="method-selection">
        <h4>Method Selection</h4>
        <p className="method-selection-description">
          Select one or more methods for age assessment.
        </p>

        <Row gutter={[16, 16]}>
          {AVAILABLE_METHODS.map((method, index) => {
            return (
              <Col md={6} key={index}>
                <Method
                  item={method}
                  selectedMethod={selectedMethod}
                  status={getMethodStatus(method.id)}
                  onClick={() => handleMethodToggle(method.id)}
                />
              </Col>
            );
          })}
        </Row>
        <br />
        <Row gutter={[16, 16]}>
          {examMethodData.map((item, index) => (
            <Col
              key={index}
              style={{
                flex: "1",
                cursor: "default", // Ensures default cursor
              }}
            >
              <SingleTooth
                isSelected={index === currentToothIndex}
                item={item}
                onClick={() => handleOnChangeTooth(index)}
                isToothStageMatched={isToothStageMatched(item.id)}
              />
            </Col>
          ))}
        </Row>

        <br />
        <h2>Stage Analysis</h2>
        <Paragraph type="secondary" style={{ marginBottom: 6 }}>
          Select a tooth development stage that best matches the case
          radiograph.
        </Paragraph>
        {additional_images?.length === 0 &&
        (!currentCaseDetail?.opg || currentCaseDetail.opg.trim() == "") ? (
          <Row gutter={[16, 16]}>
            <Col md={8}>
              <StageCriteria
                stage={currentStage}
                onStageMatched={() => handleStageMatch(currentStage.id)}
                stageMatched={isStageMatched(currentStage.id)}
              />
            </Col>
            <Col md={16}>
              <MethodReference
                onFullScreen={toggleFullscreen}
                imageContainerRef={imageContainerRef}
                data={stages}
                onChangeItem={(e) => setCurrenStageIndex(e)}
                currentStageIndex={currentStageIndex}
              />
            </Col>
          </Row>
        ) : (
          <Row gutter={[16, 16]}>
            <Col md={12}>
              <MethodReference
                onFullScreen={toggleFullscreen}
                imageContainerRef={imageContainerRef}
                data={stages}
                onChangeItem={(e) => setCurrenStageIndex(e)}
                currentStageIndex={currentStageIndex}
              />
              <StageCriteria
                stage={currentStage}
                onStageMatched={() => handleStageMatch(currentStage.id)}
                stageMatched={isStageMatched(currentStage.id)}
              />
            </Col>

            <Col md={12}>
              <PhysicalRadioGraph
                onFullScreen={toggleRadioGrapghFullscreen}
                radioGraphContainerRef={radioGraphContainerRef}
              />
            </Col>
          </Row>
        )}

        <br />
        <MatchTeethStageTable
          matchedStages={matchedStages}
          onDelete={handleDeleteMatchStageRecord}
          AVAILABLE_METHODS={AVAILABLE_METHODS}
          examMethodData={examMethodData}
          stages={stages}
          onTabClick={handleClickOnTableTab}
        />

        <Row
          gutter={[16, 16]}
          justify={"space-between"}
          style={{ padding: "0 10px" }}
        >
          <CustomButtonWithIcon
            title="Cancel"
            icon={assessmentIcons.cancel}
            color={"#DC1919"}
            onClick={onCancel}
          />
          <CustomPrimaryButton
            title="Proceed"
            icon={assessmentIcons.proceed}
            onClick={handleSubmit}
            disabled={Object.keys(matchedStages).length === 0 || isLoading}
            isLoading={isLoading}
          />
        </Row>
      </section>
    </div>
  );
}

export default WisdomTeethStaging;
